<template>
  <div class="view-page-container">
    <PremiumPage
      title="Poll Results"
      subtitle="Live voting results and poll details"
      showBack
    >
      <template #header-actions>
        <v-btn text class="pp-action-btn mr-2" @click="exportPdf" v-if="poll.status === 'expired'">
          <v-icon size="15" class="mr-1">mdi-file-pdf-box</v-icon>Export PDF
        </v-btn>
        <v-btn text class="pp-action-btn" @click="exportExcel" v-if="poll.status === 'expired'">
          <v-icon size="15" class="mr-1">mdi-file-excel</v-icon>Export Excel
        </v-btn>
      </template>

      <!-- Loading -->
      <div v-if="loading" class="state-center py-16">
        <v-progress-circular indeterminate color="var(--brand)" size="40" />
        <div class="mt-3" style="font-size:13px;color:var(--tx3);">Loading poll...</div>
      </div>

      <!-- Empty -->
      <div v-else-if="!poll" class="state-center py-16">
        <v-icon size="56" color="#CBD5E1" class="mb-3">mdi-poll</v-icon>
        <div class="empty-title">Poll not found</div>
      </div>

      <!-- Content -->
      <div v-else>

        <!-- Question Header (full width) -->
        <div class="vp-question-header mx-3 mt-2 mb-3">
          <div class="vp-question-label">Question</div>
          <div class="vp-question-text">{{ poll.title }}</div>
          <div v-if="poll.description" class="vp-question-desc mt-1">{{ poll.description }}</div>
        </div>

        <!-- Main Content: Chart + Members side by side -->
        <div class="vp-main-card mx-3 mb-3">

          <!-- Header -->
          <div v-if="!(poll.type === 'text' && totalVotes > 0)" class="d-flex align-center mb-4">
            <div class="vp-section-label">Live Results</div>
            <div v-if="poll.status === 'active'" class="vp-live-chip ml-2">
              <span class="vp-live-dot"></span> LIVE
            </div>
          </div>

          <v-row v-if="!(poll.type === 'text' && totalVotes > 0)">
            <v-col cols="12" md="10" lg="6">
              <!-- Choice Results -->
              <div v-if="poll.type === 'single_choice' || poll.type === 'multiple_choice'" class="mb-6">
                <div v-if="totalVotes > 0">
                  <div class="vp-results-grid">
                    <div class="vp-chart-wrap">
                      <PieChart :chart-data="chartData" :options="chartOptions" style="width:100%;height:100%;" />
                      <div class="vp-chart-total">
                        <div class="vp-chart-total-num">{{ totalVotes }}</div>
                        <div class="vp-chart-total-lbl">Votes</div>
                      </div>
                    </div>
                    <div class="vp-bars-wrap">
                      <div v-for="(opt, index) in liveResults.options" :key="opt.id" class="vp-bar-item">
                        <div class="vp-bar-top">
                          <div class="vp-bar-label">
                            <span class="vp-bar-color-dot" :style="{ background: getColor(index) }"></span>
                            {{ opt.title }}
                          </div>
                          <div class="vp-bar-stat">
                            <span class="vp-bar-pct" :style="{ color: getColor(index) }">{{ opt.percentage }}%</span>
                            <span class="vp-bar-count">{{ opt.count }} vote{{ opt.count !== 1 ? 's' : '' }}</span>
                          </div>
                        </div>
                        <div class="vp-bar-track">
                          <div class="vp-bar-fill" :style="{ width: opt.percentage + '%', background: getColor(index) }"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="vp-empty">
                  <v-icon size="48" color="#CBD5E1">mdi-chart-pie-outline</v-icon>
                  <div class="vp-empty-title">No votes yet</div>
                  <div class="vp-empty-sub">Results will appear here once members start voting</div>
                </div>
              </div>

              <!-- Text / Number Results -->
              <div v-else class="mb-6">
                <div v-if="totalVotes > 0">
                  <!-- Number stats -->
                  <div v-if="poll.type === 'number'" class="vp-stats-row">
                    <div class="vp-stat-box">
                      <div class="vp-stat-label">Average</div>
                      <div class="vp-stat-val" style="color: var(--brand)">{{ liveResults.average }}</div>
                    </div>
                    <div class="vp-stat-box">
                      <div class="vp-stat-label">Minimum</div>
                      <div class="vp-stat-val">{{ liveResults.min }}</div>
                    </div>
                    <div class="vp-stat-box">
                      <div class="vp-stat-label">Maximum</div>
                      <div class="vp-stat-val">{{ liveResults.max }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="vp-empty">
                  <v-icon size="48" color="#CBD5E1">mdi-comment-text-multiple-outline</v-icon>
                  <div class="vp-empty-title">No responses yet</div>
                  <div class="vp-empty-sub">Responses will appear here once members submit</div>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- COMMON MEMBERS TABLE FOR ALL TYPES (At the bottom) -->
          <v-row v-if="totalVotes > 0">
            <v-col cols="12" md="12" lg="8">
              <!-- Non-anonymous: member-wise table with Name, Unit, Answer -->
              <template v-if="!poll.is_anonymous && liveResults.voters && liveResults.voters.length > 0">
                <div class="vp-section-label mb-3">Members who Voted</div>
                <div class="vp-members-table-wrap">
                  <table class="vp-members-table">
                    <thead>
                      <tr>
                        <th style="width:40px; text-align:center;">#</th>
                        <th style="width:250px;">Name</th>
                        <th style="width:120px;">Unit</th>
                        <th>Answer</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(voter, vi) in liveResults.voters" :key="voter.id || vi">
                        <td style="text-align:center; color:#94a3b8; font-size:12px;">{{ vi + 1 }}</td>
                        <td>
                          <div style="display:flex; align-items:center; gap:8px;">
                            <v-avatar size="28" :style="{ background: primaryColor + '20', color: primaryColor }">
                              <v-img v-if="voter.profile_picture" :src="apiUrl + '/uploads/' + voter.profile_picture"></v-img>
                              <span v-else style="font-size:11px; font-weight:700;">{{ voter.name ? voter.name.substring(0,1).toUpperCase() : '?' }}</span>
                            </v-avatar>
                            <span class="vp-voter-name">{{ voter.name }}</span>
                          </div>
                        </td>
                        <td>
                          <span v-if="voter.flat_name || voter.house_number" class="vp-voter-unit">
                            {{ `${voter.flat_name || ''}${voter.flat_name && voter.house_number ? '-' : ''}${voter.house_number || ''}` }}
                          </span>
                          <span v-else style="color:#94a3b8;">-</span>
                        </td>
                        <td style="color:#475569; font-size:13.5px;">{{ voter.answer || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>

              <!-- Anonymous OR no voter data fallback -->
              <template v-else-if="poll.type === 'text' || poll.type === 'number'">
                <div class="vp-section-label mb-3">Recent Answers</div>
                <div class="vp-answers-list">
                  <div v-for="(ans, i) in liveResults.answers" :key="i" class="vp-answer-item">
                    <v-icon size="14" color="var(--brand)" class="mr-2">mdi-comment-text-outline</v-icon>
                    {{ ans }}
                  </div>
                </div>
              </template>
            </v-col>
          </v-row>
        </div>
      </div>
    </PremiumPage>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import PremiumPage from "../../common/PremiumPage.vue";
import PieChart from "../PieChart.js";
import io from "socket.io-client";

export default {
  name: "ViewPoll",
  components: { PremiumPage, PieChart },

  data() {
    return {
      loading: true,
      poll: null,
      liveResults: {},
      socket: null,
      chartColors: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#14B8A6"],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 68,
        legend: { display: false },
        tooltips: {
          backgroundColor: "#fff",
          titleFontColor: "#0F172A",
          bodyFontColor: "#334155",
          borderColor: "rgba(0,0,0,0.06)",
          borderWidth: 1,
          cornerRadius: 8,
          xPadding: 12,
          yPadding: 10,
        },
      },
    };
  },

  computed: {
    ...mapState("theme", ["primaryColor"]),
    apiUrl() {
      return process.env.VUE_APP_API_URL || "http://localhost:3333";
    },
    totalVotes() {
      return this.liveResults.total_votes || 0;
    },
    chartData() {
      if (!this.liveResults.options) return { labels: [], datasets: [] };
      return {
        labels: this.liveResults.options.map(o => o.title),
        datasets: [{
          data: this.liveResults.options.map(o => o.count),
          backgroundColor: this.liveResults.options.map((_, i) => this.getColor(i)),
          borderWidth: 0,
        }],
      };
    },
  },

  created() {
    this.fetchPollData();
    this.initSocketConnection();
  },

  beforeDestroy() {
    if (this.socket) {
      this.socket.emit("leave_poll_room", { poll_id: this.$route.params.id });
      this.socket.disconnect();
    }
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    async fetchPollData() {
      try {
        const pollId = this.$route.params.id;
        const res = await this.$axios.get(`/societyadmin/polls/${pollId}`);
        if (res.data.status) {
          this.poll = res.data.data;
          
          // Fetch results from separate API (using web endpoint for flat voters)
          const resultsRes = await this.$axios.get(`/societyadmin/polls/${pollId}/results/web`);
          if (resultsRes.data.status && resultsRes.data.data) {
            this.liveResults = resultsRes.data.data;
          }
        }
      } catch {
        this.setAlert({ showAlert: true, message: "Error loading poll details", variant: "red accent-3", timeout: 2500 });
      } finally {
        this.loading = false;
      }
    },

    initSocketConnection() {
      const pollId = this.$route.params.id;

      let token = "";
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        token = user?.token || "";
      } catch (e) {
        console.error("Error getting token:", e);
      }

      this.socket = io(process.env.VUE_APP_API_URL || "http://localhost:3333", {
        auth: { token },
        query: { token },
        transports: ["websocket", "polling"],
      });

      if (this.socket.connected) {
        this.socket.emit("join_poll_room", { poll_id: pollId });
      }

      this.socket.on("connect", () => {
        this.socket.emit("join_poll_room", { poll_id: pollId });
        // Fetch fresh data when reconnecting (e.g. after alt-tab / tab backgrounding)
        this.fetchPollData();
      });

      this.socket.on("poll_updated", (data) => {
        if (data.poll_id === pollId) {
          // Fetch complete fresh data so that voters list, text answers, etc. are also updated live
          this.fetchPollData();
        }
      });
    },

    processSocketResults(summary) {
      // Kept for backward compatibility if needed, but fetchPollData handles everything properly now
      if (this.poll.type === "single_choice" || this.poll.type === "multiple_choice") {
        const updatedOptions = this.poll.options.map(opt => {
          const optId = opt.id || opt._id;
          const count = summary.options_count[optId] || 0;
          const percentage = summary.total_votes > 0 ? Math.round((count / summary.total_votes) * 100) : 0;
          return { id: optId, title: opt.title, count, percentage };
        });
        this.liveResults = { ...this.liveResults, total_votes: summary.total_votes, options: updatedOptions };
        if (summary.voters) this.liveResults.voters = summary.voters;
      }
    },

    async exportPdf() {
      try {
        const res = await this.$axios.get(`/societyadmin/polls/${this.$route.params.id}/export/pdf`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Poll_${this.$route.params.id}_Results.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch {
        this.setAlert({ showAlert: true, message: "Failed to export PDF", variant: "red accent-3", timeout: 2500 });
      }
    },

    async exportExcel() {
      try {
        const res = await this.$axios.get(`/societyadmin/polls/${this.$route.params.id}/export/excel`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Poll_${this.$route.params.id}_Results.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch {
        this.setAlert({ showAlert: true, message: "Failed to export Excel", variant: "red accent-3", timeout: 2500 });
      }
    },

    formatDate(d) {
      if (!d) return "—";
      return new Date(d).toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },
    getTypeLabel(t) {
      return { single_choice: "Single Choice", multiple_choice: "Multiple Choice", text: "Text Answer", number: "Number Answer" }[t] || t;
    },
    getVisibilityLabel(v) {
      return { never: "Never", after_vote: "After Voting", after_poll_ends: "After Poll Ends", always: "Always" }[v] || v;
    },
    getStatusLabel(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ""; },
    getStatusColor(s) {
      return { draft: "#94A3B8", active: "#10B981", expired: "#F59E0B", deleted: "#EF4444" }[s] || "#94A3B8";
    },
    getStatusStyles(s) {
      const c = this.getStatusColor(s);
      return { background: `${c}15`, color: c, border: `1px solid ${c}30` };
    },
    getColor(i) { return this.chartColors[i % this.chartColors.length]; },
  },
};
</script>

<style scoped>
.view-page-container { padding: 24px; }

/* ── Action Buttons ── */
.pp-action-btn {
  font-size: 12.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  color: var(--tx2) !important;
  border-radius: 8px !important;
  height: 34px !important;
  border: 1px solid var(--border) !important;
  background: var(--surface2) !important;
}

/* ── States ── */
.state-center {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
}
.empty-title { font-size: 15px; font-weight: 600; color: var(--tx3); }

/* ── Card ── */
.vp-card {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 16px;
  padding: 24px;
}
.vp-divider {
  height: 1px;
  background: var(--border, #e2e8f0);
  margin: 18px 0;
}

/* ── Section Label ── */
.vp-section-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--tx4, #94A3B8);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* ── Results Header ── */
.vp-live-chip {
  display: inline-flex; align-items: center; gap: 5px;
  background: #ecfdf5; color: #10B981;
  border: 1px solid #10B98130;
  font-size: 11px; font-weight: 700;
  padding: 3px 10px; border-radius: 20px;
  letter-spacing: 0.04em;
}
.vp-live-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #10B981;
  animation: pulse 1.5s infinite;
}
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }

/* ── Question Header ── */
.vp-question-header {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 14px;
  padding: 18px 24px;
}
.vp-question-label {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--tx4, #94A3B8);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 6px;
}
.vp-question-text {
  font-size: 17px;
  font-weight: 700;
  color: var(--tx, #0f172a);
  line-height: 1.4;
}
.vp-question-desc {
  font-size: 13px;
  color: var(--tx3, #64748b);
  line-height: 1.5;
}

/* ── Main Card ── */
.vp-main-card {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 14px;
  padding: 20px 24px;
}
/* ── Choice Results Layout ── */
.vp-results-grid {
  display: flex;
  align-items: center;
  gap: 32px;
}
.vp-chart-wrap {
  position: relative;
  width: 150px;
  height: 150px;
  flex-shrink: 0;
}
.vp-chart-total {
  position: absolute; inset: 0;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  pointer-events: none;
}
.vp-chart-total-num {
  font-size: 20px; font-weight: 800;
  color: var(--tx); letter-spacing: -0.04em;
  line-height: 1;
}
.vp-chart-total-lbl { font-size: 10px; color: var(--tx4); font-weight: 600; margin-top: 2px; }

/* ── Bars ── */
.vp-bars-wrap { display: flex; flex-direction: column; gap: 12px; flex: 1; min-width: 0; }
.vp-bar-top {
  display: flex; align-items: center;
  justify-content: space-between; margin-bottom: 5px;
}
.vp-bar-label {
  display: flex; align-items: center; gap: 7px;
  font-size: 12.5px; font-weight: 600; color: var(--tx);
  flex: 1; min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.vp-bar-color-dot {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
}
.vp-bar-stat { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.vp-bar-pct { font-size: 13px; font-weight: 800; }
.vp-bar-count { font-size: 11px; color: var(--tx3); font-weight: 500; }
.vp-bar-track {
  height: 7px; border-radius: 99px;
  background: var(--surface2, #f1f5f9);
  overflow: hidden;
}
.vp-bar-fill {
  height: 100%; border-radius: 99px;
  transition: width 0.6s cubic-bezier(0.4,0,0.2,1);
}

/* ── Empty ── */
.vp-empty {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 52px 24px; gap: 8px;
}
.vp-empty-title { font-size: 15px; font-weight: 600; color: var(--tx3); }
.vp-empty-sub { font-size: 12.5px; color: var(--tx4); text-align: center; }

/* ── Stats ── */
.vp-stats-row { display: flex; gap: 12px; }
.vp-stat-box {
  flex: 1;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 14px 18px;
}
.vp-stat-label { font-size: 10.5px; font-weight: 700; color: var(--tx4); text-transform: uppercase; letter-spacing: 0.05em; }
.vp-stat-val { font-size: 24px; font-weight: 800; color: var(--tx); margin-top: 3px; }

/* ── Answers ── */
.vp-answers-list {
  max-height: 300px; overflow-y: auto;
  display: flex; flex-direction: column; gap: 7px;
}
.vp-answer-item {
  display: flex; align-items: center;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border);
  border-left: 3px solid var(--brand);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13.5px; color: var(--tx2);
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .vp-split-layout { flex-direction: column; }
  .vp-split-results, .vp-split-members { width: 100%; flex: unset; border-left: none; padding-left: 0; }
  .vp-results-grid { flex-direction: column; align-items: flex-start; }
  .vp-chart-wrap { width: 140px; height: 140px; margin: 0 auto; }
}

/* ── Voters ── */
.vp-voters-list {
  display: flex; flex-direction: column; gap: 6px;
}
.vp-voter-item {
  display: flex; align-items: center;
  background: #ffffff;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 10px;
  padding: 8px 10px;
}
.vp-voter-name {
  font-size: 13px; font-weight: 600; color: var(--tx);
}
.vp-voter-unit {
  font-size: 12px; font-weight: 700; color: var(--brand);
  background: rgba(124,58,237, 0.08);
  padding: 1px 6px; border-radius: 4px;
}

/* ── Option Group ── */
.vp-option-group {
  margin-bottom: 12px;
}
.vp-option-group-hdr {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #EFF6FF;
  border: 1px solid #BFDBFE;
  border-left: 3px solid #3B82F6;
  border-radius: 8px;
  margin-bottom: 6px;
}
.vp-option-group-title {
  font-size: 12.5px;
  font-weight: 700;
  color: #1E40AF;
  flex: 1;
}
.vp-option-group-stat {
  font-size: 11px;
  font-weight: 600;
  color: #3B82F6;
}
.vp-no-voters {
  font-size: 12px;
  color: var(--tx4);
  padding: 4px 12px;
  font-style: italic;
}

/* ── Members Table (text / number non-anonymous polls) ── */
.vp-members-table-wrap {
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid var(--border, #e2e8f0);
}
.vp-members-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.vp-members-table th {
  background: var(--surface2, #f8fafc);
  color: var(--tx4, #64748b);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 10px 12px;
  text-align: left;
  border-bottom: 2px solid var(--border, #e2e8f0);
}
.vp-members-table td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border, #e2e8f0);
  vertical-align: middle;
  color: var(--tx, #1e293b);
}
.vp-members-table tr:last-child td { border-bottom: none; }
.vp-members-table tr:nth-child(even) td { background: var(--surface2, #fcfcfd); }
</style>

